'use client'

import { useMemo, useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { cropSquareUrl } from '@sifly/shared-utils'
import { ProductGrid } from '@sifly/shared-ui/product'
import type { HomeTemplateProps } from '../types'
import {
  buildCategoryTiles,
  DEFAULT_CATEGORY,
  CATEGORY_GROUPS,
  GROUP_ALL_LABELS,
  allOrder,
  groupOrder,
  isGroupKey,
  matchesSelection,
  parseSelection,
  SUBTYPE_LABELS,
  TILE_LABELS,
  type GroupedCategory,
  type Selection,
} from './catalogTiles'

/**
 * Catalog-style home template — category-tile row at the top, product grid
 * below. The active tile filters the grid in place (URL syncs to ?category=
 * so it's deep-linkable and back-button friendly). Default selection is
 * "efoil_set" so the page leads with the marquee category; clicking
 * "All Products" or any other tile widens or switches the view. Which tiles
 * exist, their images and the grouped tiles (wings, parts & accessories) live in ./catalogTiles.
 */

// Settings is optional here (unlike the other home templates): CatalogHome
// never reads it, and the `product_catalog` content section mounts the grid
// without one. Still assignable to ComponentType<HomeTemplateProps> — a
// component requiring fewer props accepts the full set.
type CatalogHomeProps = Omit<HomeTemplateProps, 'settings'> & {
  settings?: HomeTemplateProps['settings']
}

export function CatalogHome({ products, tenant, embedded }: CatalogHomeProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const rawCategory = searchParams.get('category')
  const rawType = searchParams.get('type')
  const urlSelection = useMemo(() => parseSelection(rawCategory, rawType), [rawCategory, rawType])
  const [selection, setSelection] = useState<Selection>(urlSelection)
  const active = selection.tile

  // Keep local state in sync if user uses back/forward navigation.
  useEffect(() => {
    setSelection(urlSelection)
  }, [urlSelection])

  const tiles = useMemo(() => buildCategoryTiles(products), [products])
  const [sortBy, setSortBy] = useState<'featured' | 'price-asc' | 'price-desc'>('featured')

  const group = isGroupKey(active) ? active : null
  // The active group's categories this catalog actually carries, in listing order.
  const subtypesPresent = useMemo<GroupedCategory[]>(
    () =>
      group ? (CATEGORY_GROUPS[group] as readonly GroupedCategory[]).filter((t) => products.some((p) => p.category === t)) : [],
    [products, group],
  )

  // A type this store doesn't carry (an old link, a bookmark) would empty the
  // grid with no pressed chip to clear it; show the whole group instead.
  const subtype =
    selection.subtype !== 'all' && subtypesPresent.includes(selection.subtype) ? selection.subtype : 'all'

  const filteredAndSorted = useMemo(() => {
    const matching = products.filter((p) => matchesSelection(p, active, subtype))
    const filtered = group ? groupOrder(matching, group) : active === 'all' ? allOrder(matching) : matching
    if (sortBy === 'price-asc') {
      return [...filtered].sort((a, b) => (a.displayPrice ?? 0) - (b.displayPrice ?? 0))
    }
    if (sortBy === 'price-desc') {
      return [...filtered].sort((a, b) => (b.displayPrice ?? 0) - (a.displayPrice ?? 0))
    }
    return filtered
  }, [products, active, group, subtype, sortBy])


  function select(next: Selection) {
    setSelection(next)
    const params = new URLSearchParams(searchParams.toString())
    if (next.tile === DEFAULT_CATEGORY) {
      params.delete('category')
    } else {
      params.set('category', next.tile)
    }
    if (next.subtype === 'all') {
      params.delete('type')
    } else {
      params.set('type', next.subtype)
    }
    const qs = params.toString()
    router.push(qs ? `?${qs}` : '?', { scroll: false })
  }

  const headerTitle = active === 'all' ? 'All Products' : (TILE_LABELS[active] ?? 'All Products')
  // Embedded mode (authored /shop sections above): the page already has a
  // hero <h1> and its own document start, so the grid header must not
  // restart it — no breadcrumb, heading demoted to <h2> at the same size.
  const HeadingTag = embedded ? 'h2' : 'h1'

  return (
    <div data-template="catalog">
      <section className="px-4 py-6 max-w-[var(--max-width-page)] mx-auto">
        {/* Breadcrumb */}
        {!embedded && (
          <nav className="text-sm text-[var(--color-text-muted)] mb-3" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2">
              <li>
                <Link href="/" className="hover:text-[var(--color-text-primary)] transition-colors">
                  Homepage
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li className="text-[var(--color-text-secondary)]">{headerTitle}</li>
            </ol>
          </nav>
        )}

        {/* Page heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
          <HeadingTag
            className="text-3xl md:text-4xl font-bold text-[var(--color-text-primary)]"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {headerTitle}
          </HeadingTag>
          {tenant.seo_description && (
            <p className="text-sm text-[var(--color-text-secondary)] max-w-md">
              {tenant.seo_description}
            </p>
          )}
        </div>

        {/* Category tile row */}
        <div
          className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0"
          role="tablist"
          aria-label="Product categories"
          data-component="CategoryRow"
        >
          {tiles.map((tile) => {
            const isActive = tile.key === active
            return (
              <button
                key={tile.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => select({ tile: tile.key, subtype: 'all' })}
                className={[
                  'flex-shrink-0 w-[140px] md:w-[150px] rounded-[var(--radius-lg)] overflow-hidden border bg-[var(--color-bg-primary)] transition-all text-left',
                  isActive
                    ? 'border-[var(--color-primary)] ring-2 ring-[var(--color-primary)]/30 shadow-[var(--shadow-md)]'
                    : 'border-[var(--color-border)] hover:border-[var(--color-text-secondary)]',
                ].join(' ')}
              >
                <div className="relative aspect-square bg-[var(--color-bg-secondary)]">
                  {tile.photo ? (
                    <Image
                      src={cropSquareUrl(tile.photo) ?? tile.photo}
                      alt={tile.label}
                      fill
                      sizes="150px"
                      className="object-cover"
                    />
                  ) : (
                    // Fallback when no product in the category has a primary
                    // photo. Render a brand-tinted swatch with the first
                    // letter of the label — better than "No image" leaking
                    // into the customer view on freshly-seeded catalogs.
                    <div
                      className="w-full h-full flex items-center justify-center bg-[color-mix(in_srgb,var(--color-primary)_8%,var(--color-bg-secondary))] text-[var(--color-text-secondary)]"
                      style={{ fontFamily: 'var(--font-heading)' }}
                      aria-hidden="true"
                    >
                      <span className="text-3xl font-semibold opacity-60">
                        {tile.label.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="px-3 py-2">
                  <div className="text-sm font-medium leading-snug text-[var(--color-text-primary)] line-clamp-2">
                    {tile.label}
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        {/* Type filter: one group tile above, narrowed here by category. */}
        {group && subtypesPresent.length > 1 && (
          <div className="flex flex-wrap gap-2 mt-4" role="group" aria-label={`${TILE_LABELS[group]} type`}>
            {(['all', ...subtypesPresent] as const).map((t) => {
              const isSel = subtype === t
              return (
                <button
                  key={t}
                  type="button"
                  aria-pressed={isSel}
                  onClick={() => select({ tile: group, subtype: t })}
                  className={[
                    // Same look as the bookings FilterChip.
                    'min-h-[44px] inline-flex items-center px-4 rounded-md text-sm border transition-colors',
                    isSel
                      ? 'bg-[var(--color-selected)] text-[var(--color-selected-text)] border-[var(--color-selected)]'
                      : 'bg-transparent text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-[var(--color-text-muted)]',
                  ].join(' ')}
                >
                  {t === 'all' ? GROUP_ALL_LABELS[group] : SUBTYPE_LABELS[t]}
                </button>
              )
            })}
          </div>
        )}

        {/* Sort row */}
        <div className="flex items-center justify-between mt-6 mb-4">
          <p className="text-sm text-[var(--color-text-muted)]">
            {filteredAndSorted.length} {filteredAndSorted.length === 1 ? 'product' : 'products'}
          </p>
          <label className="text-sm text-[var(--color-text-secondary)] flex items-center gap-2">
            Sort by:
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="px-3 py-1.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-bg-primary)] text-sm text-[var(--color-text-primary)]"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
            </select>
          </label>
        </div>

        {/* Product grid */}
        {filteredAndSorted.length === 0 ? (
          <p className="text-[var(--color-text-muted)] text-sm py-12 text-center">
            No products in this category yet.
          </p>
        ) : (
          <ProductGrid products={filteredAndSorted} columns={3} />
        )}
      </section>
    </div>
  )
}
